import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BEHANCE_PROFILE = "https://be.net/shaikatpranto";

const CODE_DEFAULT_BRANDING = [
    { id: "p1", title: "Aurelia Atelier", category: "Brand Identity",
      description: "A refined identity system for an editorial fashion atelier — type, marque, palette, and stationery.",
      image: "https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?auto=format&fit=crop&w=1400&q=80",
      behance: BEHANCE_PROFILE, span: "lg:col-span-7", featured: true, visible: true, order: 0 },
    { id: "p2", title: "Northwind Coffee", category: "Logo Design",
      description: "Modern monogram and packaging direction for a specialty roastery.",
      image: "https://images.unsplash.com/photo-1559925393-8be0ec4767c8?auto=format&fit=crop&w=1200&q=80",
      behance: BEHANCE_PROFILE, span: "lg:col-span-5", featured: false, visible: true, order: 1 },
];

const CODE_DEFAULT_VIDEO = [
    { id: "v1", title: "Pujols Roofing — House Reveal", category: "Roofing Project", duration: "0:48",
      description: "A premium reveal edit of a finished roofing project.",
      poster: "https://images.unsplash.com/photo-1632935190508-bea5b34c1c9d?auto=format&fit=crop&w=1400&q=80",
      watch: "https://www.youtube.com", span: "lg:col-span-7", featured: true, visible: true, order: 0 },
];

const FALLBACK_CATEGORIES = ["All", "Brand Identity", "Logo Design", "Web Design", "Social Media Branding", "3D Visual Direction", "Creative Direction"];

const PortfolioContext = createContext({
    branding: CODE_DEFAULT_BRANDING,
    video: CODE_DEFAULT_VIDEO,
    categories: FALLBACK_CATEGORIES,
    loaded: false,
    refresh: () => {},
});

export const PortfolioProvider = ({ children }) => {
    const [branding, setBranding] = useState(CODE_DEFAULT_BRANDING);
    const [video, setVideo] = useState(CODE_DEFAULT_VIDEO);
    const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
    const [loaded, setLoaded] = useState(false);

    const fetchOnce = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/portfolio`, { timeout: 14000 });
            const d = res.data || {};
            if (Array.isArray(d.branding) && d.branding.length) setBranding(d.branding);
            if (Array.isArray(d.video) && d.video.length) setVideo(d.video);
            if (Array.isArray(d.categories) && d.categories.length) setCategories(d.categories);
        } catch (_e) {
            /* keep code defaults silently */
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        let mounted = true;
        fetchOnce().catch(() => { if (mounted) setLoaded(true); });
        return () => { mounted = false; };
    }, [fetchOnce]);

    const value = useMemo(
        () => ({ branding, video, categories, loaded, refresh: fetchOnce }),
        [branding, video, categories, loaded, fetchOnce]
    );

    return <PortfolioContext.Provider value={value}>{children}</PortfolioContext.Provider>;
};

export const usePortfolio = () => useContext(PortfolioContext);

export default PortfolioProvider;
